import React from "react";
import { Box, Sheet, Table, Grid } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import { SDelete, AddIcon } from "../../../../../styles/StyledMUI";
import DarkDatePicker from "../../../../common/DarkDatePicker";
import DarkTypography from "../../../../common/DarkTypography";
import DarkInput from "../../../../common/DarkInput";
import IconButtonCTA from "../../../../common/IconButtonCTA";
import { colorTokens, textTokens, withAlpha } from "../../../../../styles/globalStyles";
import type { Dive } from "../../../../../api/followup/Followup.typed";
import {
  diveDateToInputValue,
  formatDiveDateForDisplay,
  formatDiveMeasure,
  parseDiveDateFromInput,
} from "../../helpers/followupDiveHelpers";

type FollowUpDivesCardProps = {
  dives: Dive[];
  isLoading: boolean;
  commentAnchorRefs: React.MutableRefObject<(HTMLElement | null)[]>;
  onDiveChange: (index: number, field: keyof Dive, value: string | number | Date) => void;
  onNumericInput: (value: string) => string;
  onDeleteDive: (index: number) => void;
  onAddDive: () => void;
  onCommentClick: (event: React.MouseEvent<HTMLDivElement>, index: number, comment: string) => void;
  readOnly?: boolean;
};

type MeasureField = "DiveDepth" | "DiveTime";

const MEASURE_UNITS: Record<MeasureField, string> = { DiveDepth: "m", DiveTime: "min" };

function handleDiveDateChange(
  index: number,
  value: string,
  onDiveChange: FollowUpDivesCardProps["onDiveChange"],
) {
  const parsed = parseDiveDateFromInput(value);
  if (parsed) {
    onDiveChange(index, "Date", parsed);
  }
}

function measureInputValue(value: number | null): string {
  return typeof value === "number" && value > 0 ? value.toString() : "";
}

const diveTableInputSx = {
  width: "100%",
  minWidth: 0,
  textAlign: "center",
  "& input": {
    textAlign: "center",
  },
};

const diveTableCellSx = {
  verticalAlign: "middle",
  padding: "12px 10px",
  textAlign: "center",
} as const;

const diveTableHeaderCellSx = {
  padding: "10px 10px",
  textAlign: "center",
} as const;

const diveCommentPreviewSx = {
  overflow: "hidden",
  minWidth: 0,
  "& input": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

const diveReadOnlyValueSx = {
  fontSize: "0.8rem",
  overflowWrap: "anywhere",
} as const;

const diveReadOnlyCommentSx = {
  ...diveReadOnlyValueSx,
  whiteSpace: "pre-wrap",
} as const;

const mobileFieldLabelSx = {
  fontWeight: "bold",
  mb: 0.5,
  fontSize: "0.8rem",
} as const;

function ReadOnlyValue({
  children,
  multiline = false,
}: React.PropsWithChildren<{ multiline?: boolean }>) {
  return (
    <DarkTypography level="body-sm" sx={multiline ? diveReadOnlyCommentSx : diveReadOnlyValueSx}>
      {children}
    </DarkTypography>
  );
}

export default function FollowUpDivesCard({
  dives,
  isLoading,
  commentAnchorRefs,
  onDiveChange,
  onNumericInput,
  onDeleteDive,
  onAddDive,
  onCommentClick,
  readOnly = false,
}: FollowUpDivesCardProps) {
  const renderDate = (dive: Dive, index: number, sx: SxProps) =>
    readOnly ? (
      <ReadOnlyValue>{formatDiveDateForDisplay(dive.Date)}</ReadOnlyValue>
    ) : (
      <DarkDatePicker
        colorScheme="light"
        value={diveDateToInputValue(dive.Date)}
        onChange={(v) => handleDiveDateChange(index, v, onDiveChange)}
        sx={sx}
        size="sm"
      />
    );

  const renderSite = (dive: Dive, index: number, sx: SxProps) =>
    readOnly ? (
      <ReadOnlyValue>{dive.DiveSite || "—"}</ReadOnlyValue>
    ) : (
      <DarkInput
        value={dive.DiveSite}
        onChange={(e) => onDiveChange(index, "DiveSite", e.target.value)}
        sx={sx}
        size="sm"
      />
    );

  const renderMeasure = (dive: Dive, index: number, field: MeasureField, sx: SxProps) =>
    readOnly ? (
      <ReadOnlyValue>{formatDiveMeasure(dive[field], MEASURE_UNITS[field])}</ReadOnlyValue>
    ) : (
      <DarkInput
        type="text"
        value={measureInputValue(dive[field])}
        onChange={(e) => {
          const cleaned = onNumericInput(e.target.value);
          onDiveChange(index, field, cleaned === "" ? 0 : Number(cleaned));
        }}
        sx={sx}
        size="sm"
        endDecorator={MEASURE_UNITS[field]}
      />
    );

  const renderComment = (dive: Dive, index: number, sx: SxProps, anchor: boolean) =>
    readOnly ? (
      <ReadOnlyValue multiline>{dive.Comment || "—"}</ReadOnlyValue>
    ) : (
      <Box
        ref={
          anchor
            ? (el: HTMLDivElement | null) => {
                commentAnchorRefs.current[index] = el;
              }
            : undefined
        }
        onClick={(e: React.MouseEvent<HTMLDivElement>) =>
          onCommentClick(e, index, dive.Comment ?? "")
        }
        sx={{ minWidth: 0, overflow: "hidden" }}
      >
        <DarkInput
          value={dive.Comment ?? ""}
          readOnly
          size="sm"
          sx={
            [
              sx,
              diveCommentPreviewSx,
              { cursor: "pointer", "&:hover": { borderColor: colorTokens.border.focus } },
            ] as SxProps
          }
        />
      </Box>
    );

  const renderDelete = (index: number) =>
    readOnly ? null : (
      <IconButtonCTA
        size="sm"
        ctaVariant="danger"
        aria-label="Supprimer la plongée"
        onClick={() => onDeleteDive(index)}
      >
        <SDelete />
      </IconButtonCTA>
    );

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        backgroundColor: colorTokens.bg.sheetLight,
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        color: textTokens.color.inverse,
        "& .MuiTypography-root": { color: textTokens.color.inverse },
        "& input": { color: textTokens.color.inverse },
        "& .MuiInput-root": {
          backgroundColor: colorTokens.bg.sheetLight,
          borderColor: "#D8D8D8",
        },
        "& .MuiInput-root:hover": {
          borderColor: "#BDBDBD",
        },
        "& .MuiInput-input": {
          color: textTokens.color.inverse,
        },
        "& .MuiInput-input::placeholder": {
          color: textTokens.color.onLightMuted,
          opacity: 1,
        },
        "& .MuiInput-endDecorator": {
          color: textTokens.color.onLightSecondary,
        },
        "& input[type='date']::-webkit-calendar-picker-indicator": {
          cursor: "pointer",
          opacity: 1,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          backgroundColor: colorTokens.bg.sheetLight,
          color: textTokens.color.inverse,
        }}
      >
        <DarkTypography level="h3" sx={{ fontSize: { xs: "1rem", sm: "1.5rem" } }}>
          Plongées
        </DarkTypography>
        {!readOnly && (
          <IconButtonCTA onClick={onAddDive} ctaVariant="accent" aria-label="Ajouter une plongée">
            <AddIcon />
          </IconButtonCTA>
        )}
      </Box>

      {isLoading ? (
        <DarkTypography>Chargement...</DarkTypography>
      ) : dives.length === 0 ? (
        <DarkTypography>Aucune plongée enregistrée</DarkTypography>
      ) : (
        <>
          <Sheet
            variant="outlined"
            sx={{
              display: { xs: "none", sm: "block" },
              borderRadius: "sm",
              overflow: "auto",
              maxWidth: "100%",
              backgroundColor: colorTokens.bg.sheetLight,
              "& table": { backgroundColor: colorTokens.bg.sheetLight },
              "& thead": { backgroundColor: colorTokens.bg.sheetLight },
              "& th": {
                backgroundColor: colorTokens.bg.sheetLight,
                color: textTokens.color.inverse,
                borderColor: "#E0E0E0",
                ...diveTableHeaderCellSx,
              },
              "& tbody tr": { backgroundColor: colorTokens.bg.sheetLight },
              "& td": {
                backgroundColor: colorTokens.bg.sheetLight,
                color: textTokens.color.inverse,
                borderColor: "#E0E0E0",
                ...diveTableCellSx,
              },
            }}
          >
            <Table
              sx={{
                tableLayout: "fixed",
                backgroundColor: colorTokens.bg.sheetLight,
                "--TableCell-headBackground": colorTokens.bg.sheetLight,
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: "5%" }}>№</th>
                  <th style={{ width: "12%" }}>Date</th>
                  <th style={{ width: "20%" }}>Site</th>
                  <th style={{ width: "10%" }}>Prof.</th>
                  <th style={{ width: "10%" }}>Temps</th>
                  <th style={{ width: "15%" }}>Moniteur</th>
                  <th style={{ width: readOnly ? "28%" : "23%" }}>Commentaire</th>
                  {!readOnly && <th style={{ width: "5%" }}></th>}
                </tr>
              </thead>
              <tbody>
                {dives.map((dive, index) => (
                  <tr key={dive.ID ?? index}>
                    <td>{dive.Number}</td>
                    <td>{renderDate(dive, index, diveTableInputSx)}</td>
                    <td>{renderSite(dive, index, diveTableInputSx)}</td>
                    <td>{renderMeasure(dive, index, "DiveDepth", diveTableInputSx)}</td>
                    <td>{renderMeasure(dive, index, "DiveTime", diveTableInputSx)}</td>
                    <td>
                      <DarkTypography
                        level="body-sm"
                        sx={{ width: "100%", fontSize: "0.75rem", textAlign: "center" }}
                      >
                        {dive.Instructor}
                      </DarkTypography>
                    </td>
                    <td>{renderComment(dive, index, diveTableInputSx, true)}</td>
                    {!readOnly && (
                      <td>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          {renderDelete(index)}
                        </Box>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Sheet>

          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            {dives.map((dive, index) => (
              <Box
                key={dive.ID ?? index}
                sx={{
                  mb: 2,
                  position: "relative",
                  ":last-of-type": { mb: 0 },
                  fontSize: "0.85rem",
                  backgroundColor: colorTokens.bg.sheetLight,
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                }}
              >
                <Box sx={{ pt: 1, pb: 1, px: 1.5 }}>
                  <Box
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <DarkTypography
                      level="title-lg"
                      sx={{
                        backgroundColor: withAlpha(textTokens.color.inverse, 0.08),
                        width: 26,
                        height: 26,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        fontSize: "0.9rem",
                      }}
                    >
                      {dive.Number}
                    </DarkTypography>
                    {renderDelete(index)}
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <DarkTypography level="body-sm" sx={mobileFieldLabelSx}>
                      Date
                    </DarkTypography>
                    {renderDate(dive, index, { width: "100%" })}
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <DarkTypography level="body-sm" sx={mobileFieldLabelSx}>
                      Site
                    </DarkTypography>
                    {renderSite(dive, index, { width: "100%" })}
                  </Box>

                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid xs={6}>
                      <DarkTypography level="body-sm" sx={mobileFieldLabelSx}>
                        Profondeur
                      </DarkTypography>
                      {renderMeasure(dive, index, "DiveDepth", { width: "100%" })}
                    </Grid>
                    <Grid xs={6}>
                      <DarkTypography level="body-sm" sx={mobileFieldLabelSx}>
                        Temps
                      </DarkTypography>
                      {renderMeasure(dive, index, "DiveTime", { width: "100%" })}
                    </Grid>
                  </Grid>

                  <Box sx={{ mb: 1 }}>
                    <DarkTypography level="body-sm" sx={mobileFieldLabelSx}>
                      Moniteur
                    </DarkTypography>
                    <DarkTypography level="body-sm" sx={{ fontSize: "0.8rem" }}>
                      {dive.Instructor}
                    </DarkTypography>
                  </Box>

                  <Box>
                    <DarkTypography level="body-sm" sx={mobileFieldLabelSx}>
                      Commentaire
                    </DarkTypography>
                    {renderComment(dive, index, { width: "100%", fontSize: "0.8rem" }, false)}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
